import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Bell, CheckCircle, XCircle, UploadSimple, SignIn, Info, Trash, ArrowClockwise } from "@phosphor-icons/react";

const ICONS = {
    party_login: SignIn,
    slip_upload: UploadSimple,
    slip_verified: CheckCircle,
    slip_rejected: XCircle,
    slip_updated: Info,
};

const ACCENT = {
    party_login: "text-sky-600 bg-sky-50",
    slip_upload: "text-amber-600 bg-amber-50",
    slip_verified: "text-emerald-600 bg-emerald-50",
    slip_rejected: "text-rose-600 bg-rose-50",
    slip_updated: "text-violet-600 bg-violet-50",
};

const TYPE_LABEL = {
    party_login: "Party sign-in",
    slip_upload: "Slip upload",
    slip_verified: "Slip verified",
    slip_rejected: "Slip rejected",
    slip_updated: "Slip updated",
};

const formatWhen = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    const p = (n) => String(n).padStart(2, "0");
    return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

export default function Notifications() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all"); // all | unread

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/notifications", { params: { limit: 200 } });
            setItems(Array.isArray(data) ? data : []);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => {
        load();
        const t = setInterval(load, 20000);
        return () => clearInterval(t);
    }, [load]);

    const markAllRead = async () => {
        try {
            await api.post("/notifications/mark-read", {});
            setItems((prev) => prev.map((n) => ({ ...n, read: true })));
            toast.success("All notifications marked as read");
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const clearAll = async () => {
        if (!window.confirm("Delete all notifications? This can't be undone.")) return;
        try {
            const { data } = await api.delete("/notifications/clear");
            setItems([]);
            toast.success(`Cleared ${data.deleted ?? 0} notifications`);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const markOne = async (id) => {
        try {
            await api.post("/notifications/mark-read", { ids: [id] });
            setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
        } catch (_e) { /* ignore */ }
    };

    const filtered = filter === "unread" ? items.filter((n) => !n.read) : items;
    const unreadCount = items.filter((n) => !n.read).length;

    const openTarget = (n) => {
        markOne(n.id);
        const meta = n.meta || {};
        if (n.type === "slip_upload") navigate(`/admin/uploads?status=pending${meta.utr ? `&utr=${encodeURIComponent(meta.utr)}&_open=${Date.now()}` : ""}`);
        else if (n.type === "party_login" && meta.party_id) navigate(`/admin/transactions?party_id=${meta.party_id}`);
        else if ((n.type === "slip_verified" || n.type === "slip_rejected" || n.type === "slip_updated") && meta.utr) navigate(`/admin/uploads?utr=${encodeURIComponent(meta.utr)}&_open=${Date.now()}`);
    };

    return (
        <div data-testid="notifications-page" className="space-y-5">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <h1 className="font-display text-2xl font-bold text-slate-900">Notifications</h1>
                    <p className="mt-1 text-sm text-slate-500">
                        Real-time activity: party sign-ins, new slip uploads, verification decisions.
                    </p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Button variant="outline" size="sm" onClick={load} data-testid="notif-refresh">
                        <ArrowClockwise size={14} className="mr-1.5" /> Refresh
                    </Button>
                    <Button variant="outline" size="sm" onClick={markAllRead} disabled={unreadCount === 0} data-testid="notif-mark-all">
                        <CheckCircle size={14} className="mr-1.5" /> Mark all read
                    </Button>
                    <Button variant="outline" size="sm" onClick={clearAll} disabled={items.length === 0} data-testid="notif-clear-all" className="text-rose-600 hover:bg-rose-50">
                        <Trash size={14} className="mr-1.5" /> Clear all
                    </Button>
                </div>
            </div>

            <div className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white p-1 shadow-sm w-fit">
                {[
                    { key: "all", label: `All (${items.length})` },
                    { key: "unread", label: `Unread (${unreadCount})` },
                ].map((t) => (
                    <button
                        key={t.key}
                        data-testid={`notif-filter-${t.key}`}
                        onClick={() => setFilter(t.key)}
                        className={`rounded-md px-3 py-1.5 text-xs font-semibold transition ${
                            filter === t.key ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"
                        }`}
                    >{t.label}</button>
                ))}
            </div>

            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                {loading && items.length === 0 && (
                    <div className="px-6 py-16 text-center text-sm text-slate-400">Loading…</div>
                )}
                {!loading && filtered.length === 0 && (
                    <div className="flex flex-col items-center gap-2 px-6 py-16 text-center text-slate-400">
                        <Bell size={36} weight="regular" />
                        <div className="text-sm">No notifications {filter === "unread" ? "unread" : "yet"}.</div>
                    </div>
                )}
                <ul>
                    {filtered.map((n) => {
                        const Icon = ICONS[n.type] || Bell;
                        const accent = ACCENT[n.type] || "text-slate-500 bg-slate-100";
                        return (
                            <li key={n.id} data-testid={`notif-row-${n.id}`}
                                className={`flex items-start gap-3 border-b border-slate-100 px-5 py-4 last:border-0 ${
                                    n.read ? "" : "bg-blue-50/40"
                                }`}>
                                <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${accent}`}>
                                    <Icon size={16} weight="bold" />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <div className={`text-sm font-semibold ${n.read ? "text-slate-700" : "text-slate-900"}`}>{n.title}</div>
                                        {!n.read && <span className="h-1.5 w-1.5 rounded-full bg-blue-500" />}
                                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-600">
                                            {TYPE_LABEL[n.type] || n.type}
                                        </span>
                                    </div>
                                    {n.body && <div className="mt-1 text-xs text-slate-600">{n.body}</div>}
                                    <div className="mt-1.5 text-[11px] text-slate-400">{formatWhen(n.created_at)}</div>
                                </div>
                                <div className="flex shrink-0 items-center gap-2">
                                    <button
                                        data-testid={`notif-open-${n.id}`}
                                        onClick={() => openTarget(n)}
                                        className="rounded-md border border-slate-200 px-2.5 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-100"
                                    >Open</button>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            </div>
        </div>
    );
}
